import { useCallback, useEffect, useRef, useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { Tabs, EVENT_NAME } from 'common/constants';
import { ComposedTable } from 'components';
import { useAmplitude } from 'components/AmplitudeProvider';
import { useClient } from 'components/ClientProvider';
import { CustomLoadingOverlay } from 'components/CustomLoadingOverlay';
import {
  generateCSVDocument,
  generatePDFDocument
} from 'components/PrintViewer';
import { getSelectedStoreOption } from 'components/StoreSelect/slice';
import { useFlagValue } from 'hooks';
import { useFetchNeedsAuthExportData } from 'hooks/useClientDataServices';
import {
  getSelectedTab,
  getSelectedTabToBePaid
} from 'pages/LandingPage/components/LandingPageHeader/slice';
import {
  PaymentMethodChips,
  VendorsTableHeader
} from 'pages/VendorsTablePage/components';
import {
  DEFAULT_COL_WIDTH,
  MIN_COL_WIDTH
} from 'pages/VendorsTablePage/components/VendorsTable/constants';
import { getNumberFromCurrency } from 'pages/VendorsTablePage/components/VendorsTable/helpers';
import {
  getDueDateInFormatToDisplay,
  getPaymentMethods,
  getSearchInputValue,
  getSelectedTotal,
  getSelectedTotalDiscount,
  getVendorsResponse,
  getTotalNoOfVendors,
  setDebouncedSearchInputValue,
  setSearchInputValue
} from 'pages/VendorsTablePage/components/VendorsTable/slice';
import { StyledTableHeader } from 'pages/VendorsTablePage/components/VendorsTable/styled';
import {
  VendorDataType,
  VendorsTableDataType
} from 'pages/VendorsTablePage/components/VendorsTable/types';
import { useVendorsTableColumnDefs } from 'pages/VendorsTablePage/components/VendorsTable/useVendorsTableColumnDefs';
import { VendorsTotalsFooter } from 'pages/VendorsTablePage/components/VendorsTotalsFooter';
import { labels } from 'pages/VendorsTablePage/messages';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { amplitudeTracker } from 'utils/AmplitudeTracker';
import {
  HIGH_VOLUME_BLOCK_LOAD_DEBOUNCE,
  GRID_INITIAL_ROW_COUNT,
  HIGH_VOLUME_VENDORS_PAGE_SIZE,
  HIGH_VOLUME_MAX_PAGES_IN_CACHE,
  HIGH_VOLUME_MIN_SEARCH_LENGTH
} from '../constants';
import { generateExcelDocument } from '../utils';
import {
  StyledHighVolumeGridContainer,
  StyledHighVolumeGridElement,
  StyledHighVolumeGridDimension
} from './styled';
import { useVendorsServerSideDatasource } from './useVendorsServerSideDatasource';

export const HighVolumeVendorsTable = (): React.JSX.Element => {
  const amplitude = useAmplitude();
  const dispatch = useAppDispatch();
  const [trackLaunchOfToBePaid, setTrackLaunchOfToBePaid] = useState(false);
  const [isExportDataQueryEnabled, setIsExportDataQueryEnabled] =
    useState(false);
  const client = useClient();
  const selectedTabToBePaid = useAppSelector(getSelectedTabToBePaid);
  const searchInputValue = useAppSelector(getSearchInputValue);
  const selectedTotalInFooter = useAppSelector(getSelectedTotal);
  const discountTotalInFooter = useAppSelector(getSelectedTotalDiscount);
  const interCompanyFlag = useFlagValue('dmsAccountingIntercompanyPayment');
  const totalOwedInFooter =
    getNumberFromCurrency(useAppSelector(getVendorsResponse)?.totalOwed) -
    selectedTotalInFooter;
  const selectedStore = useAppSelector(getSelectedStoreOption).label;
  const selectedDueDate = useAppSelector(getDueDateInFormatToDisplay);
  const selectedPaymentMethods = useAppSelector(getPaymentMethods).join(',');
  const data = useAppSelector(getVendorsResponse);
  const rowDataLength = useAppSelector(getTotalNoOfVendors);
  const selectedTab = useAppSelector(getSelectedTab);
  const intl: IntlShape = useIntl();
  const ampFlagForNeedsAuth = useRef<boolean>(true);

  const { isFilterChanged, serverSideDatasource } =
    useVendorsServerSideDatasource();
  const { autoGroupColumnDef, columnDefs } = useVendorsTableColumnDefs();

  const onSearchInputChange = useCallback(
    (newValue: string): void => {
      dispatch(setSearchInputValue(newValue));
      if (selectedTab === Tabs.ToBePaid) {
        amplitudeTracker(amplitude, EVENT_NAME.SearchVendorInvoices);
      }
    },
    [amplitude, dispatch, selectedTab]
  );

  const onDebouncedSearchInputChange = useCallback(
    (searchValue: string) => {
      dispatch(
        setDebouncedSearchInputValue(
          searchValue.length >= HIGH_VOLUME_MIN_SEARCH_LENGTH ? searchValue : ''
        )
      );
    },
    [dispatch]
  );

  useEffect(() => {
    if (amplitude && !trackLaunchOfToBePaid && selectedTab === Tabs.ToBePaid) {
      amplitudeTracker(amplitude, EVENT_NAME.ToBePaidTab);
      amplitudeTracker(amplitude, EVENT_NAME.EnterAccountsPayable);
      setTrackLaunchOfToBePaid(true);
    }
  }, [amplitude, trackLaunchOfToBePaid, setTrackLaunchOfToBePaid, selectedTab]);

  useEffect(() => {
    if (
      amplitude &&
      ampFlagForNeedsAuth.current &&
      selectedTab === Tabs.NeedsAuthorization
    ) {
      amplitudeTracker(amplitude, EVENT_NAME.ReviewAuthorization);
      ampFlagForNeedsAuth.current = false;
    }
  }, [amplitude, trackLaunchOfToBePaid, setTrackLaunchOfToBePaid, selectedTab]);

  useEffect(() => {
    dispatch(setSearchInputValue(''));
    dispatch(setDebouncedSearchInputValue(''));
  }, [dispatch, selectedTab]);

  const { data: exportData, isFetching: isExportDataFetching } =
    useFetchNeedsAuthExportData(isExportDataQueryEnabled);

  useEffect(() => {
    if (exportData && !isExportDataFetching) {
      setIsExportDataQueryEnabled(false);
    }
  }, [exportData, isExportDataFetching]);

  return (
    <>
      <StyledHighVolumeGridContainer>
        <StyledHighVolumeGridElement>
          <StyledHighVolumeGridDimension>
            <ComposedTable<VendorsTableDataType, VendorDataType>
              autoGroupColumnDef={autoGroupColumnDef}
              columnDefs={columnDefs}
              customHeaderCount={rowDataLength}
              defaultColDef={{
                width: DEFAULT_COL_WIDTH,
                minWidth: MIN_COL_WIDTH
              }}
              downloadDialogTitle={intl.formatMessage(
                labels.needsAuthorizationTitle
              )}
              customHeight={client?.isMFE ? 'calc(100vh - 230px)' : undefined}
              exportData={exportData}
              getCSVDocument={(gridData, totalsInFooter, tableHeaderFilters) =>
                generateCSVDocument(gridData, {
                  selectedTab,
                  intl,
                  totalsInFooter,
                  tableHeaderFilters
                })
              }
              getExcelDocument={generateExcelDocument}
              getPDFDocument={(
                gridData,
                gridRef,
                convertToBlob,
                totalsInFooter,
                tableHeaderFilters
              ) =>
                generatePDFDocument(
                  gridData,
                  gridRef,
                  convertToBlob,
                  {
                    selectedTab,
                    intl,
                    totalsInFooter,
                    tableHeaderFilters
                  },
                  interCompanyFlag
                )
              }
              getRowId={param => param.data?.id}
              invoiceCount={data?.totalNoOfInvoices}
              isDownloadable={!selectedTabToBePaid}
              isExportDataFetching={isExportDataFetching}
              isPrintable={!selectedTabToBePaid}
              isSearchable
              loadingOverlayComponent={CustomLoadingOverlay}
              rowModelType="serverSide"
              serverSideDatasource={serverSideDatasource}
              cacheBlockSize={HIGH_VOLUME_VENDORS_PAGE_SIZE}
              maxBlocksInCache={HIGH_VOLUME_MAX_PAGES_IN_CACHE}
              blockLoadDebounceMillis={HIGH_VOLUME_BLOCK_LOAD_DEBOUNCE}
              onDebouncedSearchInputChange={onDebouncedSearchInputChange}
              onSearchInputChange={onSearchInputChange}
              purgeClosedRowNodes={false}
              reactiveCustomComponents
              serverSidePivotResultFieldSeparator="_"
              serverSideOnlyRefreshFilteredGroups={false}
              serverSideInitialRowCount={GRID_INITIAL_ROW_COUNT}
              scrollToTopButtonShow={false}
              searchInputValue={searchInputValue}
              setIsExportDataQueryEnabled={setIsExportDataQueryEnabled}
              suppressAggFuncInHeader
              suppressMultiSort
              suppressRowClickSelection
              suppressRowDeselection
              suppressServerSideFullWidthLoadingRow={isFilterChanged}
              tableHeaderTitle={intl.formatMessage(labels.tableTitle)}
              tableHeaderContent={
                <StyledTableHeader>
                  <VendorsTableHeader />
                  <PaymentMethodChips />
                </StyledTableHeader>
              }
              tableHeaderFilters={{
                selectedStore,
                selectedDueDate,
                selectedPaymentMethods
              }}
              totalsInFooter={{
                selectedTotalInFooter,
                discountTotalInFooter,
                totalOwedInFooter
              }}
              useCustomSearchFilter={false}
            />
          </StyledHighVolumeGridDimension>
        </StyledHighVolumeGridElement>
      </StyledHighVolumeGridContainer>
      <VendorsTotalsFooter data={data ?? undefined} />
    </>
  );
};
